import React, { useState, useEffect } from 'react';
import AdminLayout from '../layouts/AdminLayout';
import adminApi from '../api/adminApi';
import { FaPlus, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';

const EMPTY = { title: '', slug: '', icon: '🏥', shortDescription: '', fullDescription: '', subServices: '', isActive: true, sortOrder: 0 };

const AdminServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [error, setError] = useState('');

  const fetchServices = async () => {
    try { const res = await adminApi.get('/services/all'); setServices(res.data); }
    catch { setError('Failed to load services.'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchServices(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setModal('add'); setError(''); };
  const openEdit = svc => {
    setForm({ title: svc.title, slug: svc.slug, icon: svc.icon, shortDescription: svc.shortDescription, fullDescription: svc.fullDescription, subServices: svc.subServices, isActive: svc.isActive, sortOrder: svc.sortOrder });
    setEditId(svc.id); setModal('edit'); setError('');
  };
  const closeModal = () => { setModal(null); setError(''); };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(p => ({ ...p, [name]: type === 'checkbox' ? checked : value }));
  };

  const autoSlug = title => title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  const handleTitleChange = e => {
    const val = e.target.value;
    setForm(p => ({ ...p, title: val, slug: p.slug || autoSlug(val) }));
  };

  const handleSave = async () => {
    if (!form.title) { setError('Title is required.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { ...form, sortOrder: parseInt(form.sortOrder) || 0 };
      if (modal === 'add') await adminApi.post('/services', payload);
      else await adminApi.put(`/services/${editId}`, payload);
      await fetchServices();
      closeModal();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save.'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await adminApi.delete(`/services/${deleteId}`); setDeleteId(null); await fetchServices(); }
    catch { setError('Failed to delete.'); setDeleteId(null); }
  };

  const filtered = services.filter(s =>
    s.title?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout title="Services" subtitle="Manage hospital specialty services">
      <div className="admin-card" id="admin-services-card">
        <div className="admin-card-header">
          <div className="admin-card-title">All Services ({filtered.length})</div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <div className="admin-search">
              <FaSearch className="admin-search-icon" />
              <input id="service-search" placeholder="Search services…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
            <button className="admin-btn admin-btn-primary" onClick={openAdd} id="service-add-btn"><FaPlus /> Add Service</button>
          </div>
        </div>

        <div className="admin-table-wrapper">
          <table className="admin-table" id="services-table">
            <thead><tr><th>Icon</th><th>Title</th><th>Slug</th><th>Sub-Services</th><th>Order</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7}><div className="admin-empty"><div className="admin-empty-icon">🏥</div><div className="admin-empty-text">No services found.</div></div></td></tr>
              ) : filtered.map(svc => (
                <tr key={svc.id} id={`service-row-${svc.id}`}>
                  <td style={{ fontSize: '1.5rem', textAlign: 'center' }}>{svc.icon}</td>
                  <td>
                    <div style={{ fontWeight: 700 }}>{svc.title}</div>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: '0.2rem' }}>{svc.shortDescription?.substring(0, 60)}…</div>
                  </td>
                  <td><code style={{ fontSize: '0.75rem', background: '#f1f5f9', padding: '0.2rem 0.4rem', borderRadius: 4 }}>{svc.slug}</code></td>
                  <td style={{ fontSize: '0.8rem', color: '#64748b' }}>{svc.subServices?.split(',').length || 0} items</td>
                  <td style={{ textAlign: 'center' }}>{svc.sortOrder}</td>
                  <td><span className={`admin-badge ${svc.isActive ? 'active' : 'inactive'}`}>{svc.isActive ? '● Active' : '● Inactive'}</span></td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-btn admin-btn-edit admin-btn-sm" onClick={() => openEdit(svc)} id={`edit-service-${svc.id}`}><FaEdit /></button>
                      <button className="admin-btn admin-btn-danger admin-btn-sm" onClick={() => setDeleteId(svc.id)} id={`delete-service-${svc.id}`}><FaTrash /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {(modal === 'add' || modal === 'edit') && (
        <div className="admin-modal-overlay" id="service-modal-overlay" onClick={closeModal}>
          <div className="admin-modal admin-modal-lg" onClick={e => e.stopPropagation()} id="service-modal">
            <div className="admin-modal-header">
              <div className="admin-modal-title">{modal === 'add' ? 'Add New Service' : 'Edit Service'}</div>
              <button className="admin-modal-close" onClick={closeModal}>✕</button>
            </div>
            <div className="admin-modal-body">
              {error && <div className="admin-login-error" style={{ marginBottom: '1rem' }}>{error}</div>}
              <div className="admin-form-grid">
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="svc-title">Title *</label>
                  <input id="svc-title" name="title" className="admin-form-input" placeholder="Service name" value={form.title} onChange={handleTitleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="svc-icon">Icon (emoji)</label>
                  <input id="svc-icon" name="icon" className="admin-form-input" placeholder="🏥" value={form.icon} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="svc-slug">Slug (URL key)</label>
                  <input id="svc-slug" name="slug" className="admin-form-input" placeholder="auto-generated" value={form.slug} onChange={handleChange} />
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label" htmlFor="svc-order">Sort Order</label>
                  <input id="svc-order" name="sortOrder" type="number" className="admin-form-input" placeholder="1" value={form.sortOrder} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="svc-short">Short Description</label>
                  <input id="svc-short" name="shortDescription" className="admin-form-input" placeholder="Brief one-line description" value={form.shortDescription} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="svc-full">Full Description</label>
                  <textarea id="svc-full" name="fullDescription" className="admin-form-textarea" rows={5} placeholder="Detailed description of the service…" value={form.fullDescription} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-form-label" htmlFor="svc-subservices">Sub-Services (comma-separated)</label>
                  <textarea id="svc-subservices" name="subServices" className="admin-form-textarea" rows={3} placeholder="Service A,Service B,Service C" value={form.subServices} onChange={handleChange} />
                </div>
                <div className="admin-form-group full">
                  <label className="admin-toggle" htmlFor="svc-active">
                    <input id="svc-active" type="checkbox" name="isActive" checked={form.isActive} onChange={handleChange} />
                    <div className="admin-toggle-track"><div className="admin-toggle-thumb" /></div>
                    <span className="admin-form-label" style={{ marginBottom: 0 }}>Active (visible on website)</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="admin-btn admin-btn-primary" onClick={handleSave} disabled={saving} id="service-modal-save">
                {saving ? '⏳ Saving…' : modal === 'add' ? '✅ Add Service' : '✅ Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <div className="admin-modal-overlay" onClick={() => setDeleteId(null)}>
          <div className="admin-modal admin-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-body" style={{ textAlign: 'center', padding: '2.5rem 2rem' }}>
              <div className="admin-confirm-icon">🗑️</div>
              <div className="admin-confirm-title">Delete Service?</div>
              <p className="admin-confirm-text">This service will be permanently removed from the website.</p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn admin-btn-ghost" onClick={() => setDeleteId(null)}>Cancel</button>
              <button className="admin-btn admin-btn-danger" onClick={handleDelete} id="service-delete-confirm">🗑️ Delete</button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminServicesPage;
